import { getSubdomainFromHost, isOrganizationSubdomain } from './hostOrg.js'

export const DEFAULT_PUBLIC_ORG_AUTH_POLICY = {
  hasSso: false,
  organization: null,
  authMode: 'supabase_saml',
  enforceSso: false,
  allowPasswordFallback: true,
  allowSocialLogin: true,
  allowedSocialProviders: [],
  providerId: null,
  brokerProvider: null,
  brokerOrgId: null,
  brokerConnectionId: null,
  brokerEnabled: false,
  brokerPreferred: false,
}

export function normalizePublicOrgAuthPolicy(payload) {
  if (!payload || typeof payload !== 'object') {
    return { ...DEFAULT_PUBLIC_ORG_AUTH_POLICY }
  }

  return {
    ...DEFAULT_PUBLIC_ORG_AUTH_POLICY,
    hasSso: !!payload.hasSso,
    organization: payload.organization || null,
    authMode: payload.authMode || 'supabase_saml',
    enforceSso: !!payload.enforceSso,
    allowPasswordFallback: payload.allowPasswordFallback !== false,
    allowSocialLogin: payload.allowSocialLogin !== false,
    allowedSocialProviders: Array.isArray(payload.allowedSocialProviders)
      ? payload.allowedSocialProviders.map((provider) => String(provider || '').trim().toLowerCase()).filter(Boolean)
      : [],
    providerId: payload.providerId || null,
    brokerProvider: payload.brokerProvider || null,
    brokerOrgId: payload.brokerOrgId || null,
    brokerConnectionId: payload.brokerConnectionId || null,
    brokerEnabled: !!payload.brokerEnabled,
    brokerPreferred: !!payload.brokerPreferred,
  }
}

export function getHostnameFromOriginLike(value) {
  if (!value) return ''
  try {
    if (/^https?:\/\//i.test(String(value))) {
      return new URL(String(value)).hostname
    }
  } catch {
    return ''
  }
  return String(value || '').trim().toLowerCase()
}

export function isGoogleOauthAllowed(policy) {
  const normalized = normalizePublicOrgAuthPolicy(policy)
  if (!normalized.allowSocialLogin) return false
  if (!normalized.allowedSocialProviders.length) return true
  return normalized.allowedSocialProviders.includes('google')
}

// React Native defines `window` but not `window.location`.
function getCurrentHostname() {
  return globalThis.location?.hostname || ''
}

/**
 * @param {object} [options]
 * @param {string} [options.baseUrl=''] - API origin. Web passes nothing (relative `/api` path).
 * @param {typeof fetch} [options.fetchImpl=globalThis.fetch]
 */
export async function fetchPublicOrgAuthPolicy({
  hostname,
  emailDomain,
  baseUrl = '',
  fetchImpl = globalThis.fetch,
} = {}) {
  const resolvedHostname = getHostnameFromOriginLike(hostname || getCurrentHostname())
  const subdomain = getSubdomainFromHost(resolvedHostname)
  const normalizedEmailDomain = String(emailDomain || '').trim().toLowerCase()

  if (!normalizedEmailDomain && !isOrganizationSubdomain(subdomain)) {
    return { ...DEFAULT_PUBLIC_ORG_AUTH_POLICY }
  }

  const query = normalizedEmailDomain
    ? `domain=${encodeURIComponent(normalizedEmailDomain)}`
    : `subdomain=${encodeURIComponent(subdomain)}`

  try {
    const res = await fetchImpl(`${baseUrl}/api/sso/public-policy?${query}`, {
      headers: { Accept: 'application/json' },
    })
    const json = await res.json().catch(() => null)
    if (!res.ok) {
      return { ...DEFAULT_PUBLIC_ORG_AUTH_POLICY }
    }
    return normalizePublicOrgAuthPolicy(json)
  } catch {
    return { ...DEFAULT_PUBLIC_ORG_AUTH_POLICY }
  }
}

export async function assertGoogleOauthAllowed({ hostname, targetOrigin, emailDomain, baseUrl, fetchImpl } = {}) {
  const resolvedHostname = getHostnameFromOriginLike(targetOrigin)
    || getHostnameFromOriginLike(hostname || getCurrentHostname())
  const policy = await fetchPublicOrgAuthPolicy({ hostname: resolvedHostname, emailDomain, baseUrl, fetchImpl })
  if (!isGoogleOauthAllowed(policy)) {
    const error = new Error('Google login is disabled for this organization. Use email/password instead.')
    error.code = 'google_oauth_disabled'
    throw error
  }
  return policy
}
